import React, { useRef, useState } from "react";
import styles from "./ContactForm.module.scss";
import ReCAPTCHA from "react-google-recaptcha";

const RECAPTCHA_SITE_KEY = "6LcobusrAAAAANH9H7-CiqugwqaggkcjNMFF7ByW"; // або process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    consent: false,
  });

  // reCAPTCHA
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === "checkbox") {
      const target = e.target as HTMLInputElement;
      setFormData((prevData) => ({
        ...prevData,
        [name]: target.checked,
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.consent) {
      alert("Bitte bestätigen Sie Ihre Einwilligung.");
      return;
    }
    if (!captchaToken) {
      alert("Bitte bestätigen Sie, dass Sie kein Roboter sind.");
      return;
    }

    const formPayload = {
      type: "feedback",
      feedbacktype: "feedbackform",
      legid: 1,
      data: {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        message: formData.message,
        recaptchaToken: captchaToken, // ← передаємо токен (валідуйте на бекенді)
      },
    };

    try {
      setLoading(true);
      await sendToCRM(formPayload);

      // GTM подія
      (window as any).dataLayer?.push({
        event: "form_submit",
        form_name: "contact_form",
        form_type: "feedbackform",
      });

      alert("Ihre Nachricht wurde erfolgreich gesendet!");

      // Очистка
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
        consent: false,
      });
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    } catch (error) {
      alert("Etwas ist schief gelaufen. Versuchen Sie es später erneut.");
    } finally {
      setLoading(false);
    }
  };

  const sendToCRM = async (data: any) => {
    const res = await fetch("https://crm.hixa.eu/external/goodsapi/", {
      method: "POST",
      headers: {
        Authorization: "Bearer DH345DFGDGb32FDsf03876SDFSdf13",
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        Accept: "application/json",
      },
      body: new URLSearchParams({
        type: data.type,
        legid: data.legid.toString(),
        feedbacktype: data.feedbacktype,
        data: JSON.stringify(data.data),
      }),
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`Failed to send message to CRM: ${res.status} ${text}`);
    }
    return res.json();
  };

  return (
    <div className={`container top12 ${styles.contactSection}`}>
      <div className={`sideP88 sectionContainerWhite ${styles.formContainer}`}>
        <div className={styles.contactSectionBlock}>
          <div className={styles.sectionDesk}>
            <h3 className={styles.heading}>Schreiben Sie uns</h3>
            <p className={styles.subheading}>
              Wir freuen uns auf Ihre Nachricht!
            </p>
          </div>
          <form className={styles.feedbackForm} onSubmit={handleSubmit}>
            <div className={styles.doubleInput}>
              <div className={styles.formGroup}>
                <label htmlFor="name" className={styles.label}>
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  placeholder="Max Mustermann *"
                  value={formData.name}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="email" className={styles.label}>
                  E-Mail *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="max.musterman@mail.com *"
                  value={formData.email}
                  onChange={handleChange}
                  className={styles.input}
                  required
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="phone" className={styles.label}>
                Telefonnummer
              </label>
              <input
                type="text"
                id="phone"
                name="phone"
                placeholder="Ihre Telefonnummer"
                value={formData.phone}
                onChange={handleChange}
                className={styles.input}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="message" className={styles.label}>
                Ihre Nachricht *
              </label>
              <textarea
                id="message"
                name="message"
                placeholder="Ihre Nachricht an uns *"
                value={formData.message}
                onChange={handleChange}
                className={styles.textarea}
                required
              ></textarea>
            </div>

            <div className={styles.consent}>
              <input
                type="checkbox"
                id="consent"
                name="consent"
                checked={formData.consent}
                onChange={handleChange}
                className={styles.checkbox}
                required
              />
              <label htmlFor="consent">
                <span className="textBasic">
                  Ich bin damit einverstanden, dass meine personenbezogenen
                  Daten gemäß der Datenschutz-Grundverordnung (DSGVO) von der
                  HIXA GmbH, Neukirchstraße 74, 13089 Berlin, zum Zweck der
                  Bearbeitung meiner Anfrage verarbeitet und gespeichert werden.
                  Weitere Informationen finde ich in der{" "}
                </span>
                <a
                  href="/datenschutzerklarung/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Datenschutzerklärung
                </a>
                .
              </label>
            </div>

            {/* reCAPTCHA */}
            <div className={styles.formGroup}>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={(token) => setCaptchaToken(token)}
                onExpired={() => setCaptchaToken(null)}
              />
            </div>

            <button
              type="submit"
              className={`commonButton buttonMd ${styles.submitButton}`}
              disabled={loading || !formData.consent || !captchaToken}
            >
              <span>{loading ? "Senden…" : "Nachricht senden"}</span>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
